import { useTranslation } from "react-i18next";
import {useAppDispatch, useAppSelector} from "../app/hooks";
import {RootState} from "../app/store";
import {Outlet, useNavigate, useParams} from "react-router-dom";
import {useEffect} from "react";
import {RouteNames} from "../routes";
import {PatientResponseDto, UserAccountDto} from "../api/client-axios";
import {ColumnsType} from "antd/lib/table/interface";
import { Table } from "antd";

import {formatPhone} from "../utils/formatPhone";
import {
    clearNoOrgErrorPatients,
    deleteNoOrgPatient,
    fetchNoOrgPatients,
    selectNoOrgPatient,
    startDeleteNoOrgPatient, updateNoOrgSearchTerm
} from "../features/patientsNoOrgSlice";

import { CrudModals } from "../components/CrudModals";
import { CrudToolbar } from "../components/CrudToolbar";
import { EmptyState } from "../components/EmptyState";
import { TableSkeleton } from "../components/TableSkeleton";

export const PatientsIndividualsPage = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const patients = useAppSelector((state: RootState) => state.patientsNoOrg.patients);
    const selectedPatient = useAppSelector((state: RootState) => state.patientsNoOrg.selectedPatient);
    const searchTerm = useAppSelector((state: RootState) => state.patientsNoOrg.searchTerm);
    const countries = useAppSelector((state: RootState) => state.country.countries);
    const status = useAppSelector((state: RootState) => state.patientsNoOrg.status);
    const error = useAppSelector((state: RootState) => state.patientsNoOrg.error);
    const navigate = useNavigate();
    const params = useParams();

    useEffect(() => {
        if (status === 'idle') {
            dispatch(fetchNoOrgPatients({}));
        }
    });

    if (params.patientId !== undefined) {
        return <Outlet />
    }

    const onWarnDeletePatient = () => {
        if (selectedPatient != null) {
            dispatch(startDeleteNoOrgPatient());
        }
    }

    const onEditPatient = () => {
        if (selectedPatient != null) {
            navigate(`/${RouteNames.individuals}/patients/${selectedPatient.id}`);
        }
    }

    const onReload = () => {

        dispatch(fetchNoOrgPatients({}));
    }

    const onSearch = () => {
        dispatch(fetchNoOrgPatients({ q: searchTerm }));
    }

    const onRow = (patient: PatientResponseDto, index: number | undefined) => {
        return {
            onClick: () => {
                dispatch(selectNoOrgPatient(patient))
            }, // click row
        }
    }

    const onDeleteOrganizationAdmin = () => {
        if (selectedPatient != null) {
            dispatch(deleteNoOrgPatient({
                patientId: selectedPatient.id!
            }));
        }
    }

    const getRowClassName = (patient: PatientResponseDto, index: number) => {
        return selectedPatient != null && selectedPatient.id === patient.id ? 'bg-selected' : '';
    }

    const columns: ColumnsType<PatientResponseDto> = [
        {

            // width: '100%',
            title: t("column.firstName"),
            dataIndex: 'firstName',
            key: 'firstName',
            sorter: (a, b) => a.firstName! > b.firstName! ? -1 : 1,
        },
        {

            //width: '100%',
            title: t("column.lastName"),
            dataIndex: 'lastName',
            key: 'lastName',
            sorter: (a, b) => a.firstName! > b.firstName! ? -1 : 1,
        },
        {

            //width: '100%',
            title: t("column.email"),
            dataIndex: 'userAccount',
            key: 'email',
            sorter: (a, b) => a.userAccount?.emailAddress! > b.userAccount?.emailAddress! ? -1 : 1,
            render: (userAccount?: UserAccountDto) => userAccount?.emailAddress
        },
        {

            //width: '100%',
            title: t("column.phone"),
            dataIndex: 'userAccount',
            key: 'phone',
            sorter: (a, b) => a.userAccount?.mobileNumber! > b.userAccount?.mobileNumber! ? -1 : 1,
            render: (userAccount?: UserAccountDto) => formatPhone(userAccount?.mobileNumber, countries)
        },

    ];

    //todo implement server side paging and sorting
    const content = status === 'loading' || status === 'idle'
        ? <TableSkeleton />
        : <>
            <CrudToolbar
                hasSelection={selectedPatient != null}
                canEdit={selectedPatient?.id != null}
                selectionLabel={[selectedPatient?.firstName, selectedPatient?.lastName].filter(Boolean).join(" ")}
                onDelete={onWarnDeletePatient}
                onEdit={onEditPatient}
                onReload={onReload}
                onClearSelection={() => dispatch(selectNoOrgPatient(undefined))}
                search={{
                    placeholder: t("placeholder.searchPeople"),
                    value: searchTerm,
                    onChange: value => dispatch(updateNoOrgSearchTerm(value)),
                    onSearch
                }}
            />
            <div className="card card--flush">
                <Table
                    rowKey={(patient: PatientResponseDto) => patient.id!}
                    rowClassName={getRowClassName}
                    onRow={onRow}
                    dataSource={patients}
                    columns={columns}
                    locale={{ emptyText: <EmptyState /> }}
                    pagination={{ hideOnSinglePage: true, defaultPageSize: 12, showSizeChanger: false }}
                />
            </div>

            <CrudModals
                status={status}
                error={error}
                entityKey="patient"
                deleteTarget={[selectedPatient?.firstName, selectedPatient?.lastName].filter(Boolean).join(" ")}
                onDismiss={() => dispatch(clearNoOrgErrorPatients())}
                onConfirmDelete={onDeleteOrganizationAdmin}
            />
        </>

    return content;
}
